import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  notificationService,
  CommunityNotificationPreferences,
  NotificationSettings,
  UpdateNotificationSettingsDto,
} from '@/services/notification.service';
import { defaultQueryOptions } from '@/config/react-query.config';
import { toast } from 'react-hot-toast';

export const notificationKeys = {
  all: ['notifications'] as const,
  settings: () => [...notificationKeys.all, 'settings'] as const,
};

/**
 * Hook to fetch notification settings for authenticated member
 */
export const useNotificationSettings = () => {
  return useQuery<NotificationSettings, Error>({
    queryKey: notificationKeys.settings(),
    queryFn: () => notificationService.getNotificationSettings(),
    ...defaultQueryOptions,
  });
};

/**
 * Hook to update notification settings
 */
export const useUpdateNotificationSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updateDto: UpdateNotificationSettingsDto) =>
      notificationService.updateNotificationSettings(updateDto),
    onSuccess: () => {
      toast.success('Notification settings updated successfully');
      queryClient.invalidateQueries({ queryKey: notificationKeys.settings() });
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || 'Failed to update notification settings'
      );
    },
  });
};

/**
 * Update the Community slice of the member's notification settings.
 *
 * Separate from `useUpdateNotificationSettings` for two reasons:
 *
 * 1. **The API replaces `community` wholesale.** Sending `{ enabled: false }`
 *    alone would erase every per-type preference the member has set, so this
 *    merges the patch over the cached object and sends the result.
 * 2. **No success toast.** These are switches in a settings list — the switch
 *    moving is the confirmation, and a toast per tap is noise. Failures still
 *    surface, and the optimistic write is rolled back.
 */
export const useUpdateCommunityNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patch: CommunityNotificationPreferences) => {
      const current = queryClient.getQueryData<NotificationSettings>(
        notificationKeys.settings()
      );
      return notificationService.updateNotificationSettings({
        community: { ...(current?.community ?? {}), ...patch },
      });
    },
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.settings() });
      const previous = queryClient.getQueryData<NotificationSettings>(
        notificationKeys.settings()
      );
      if (previous) {
        queryClient.setQueryData<NotificationSettings>(notificationKeys.settings(), {
          ...previous,
          community: { ...(previous.community ?? {}), ...patch },
        });
      }
      return { previous };
    },
    onError: (error: any, _patch, context) => {
      if (context?.previous) {
        queryClient.setQueryData(notificationKeys.settings(), context.previous);
      }
      toast.error(
        error.response?.data?.message || 'Failed to update notification settings'
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.settings() });
    },
  });
};

export default useNotificationSettings;
